import type { PageHelp } from 'help/types';

export const paymentsHelp: PageHelp = {
  title: 'Payments',
  intro:
    'Every premium payment that has been cleared against a client payment plan, from a bank statement or from the client balance, lands here. Filter by insurer and period, then declare the payments to the insurer or generate the insurer Excel report, or invalidate a payment that was cleared by mistake.',
  sections: [
    'Getting around',
    'Finding payments',
    'Reading the list',
    'Declaring to the insurer',
    'Declare payments dialog',
    'After declaring',
    'Invalidate payments dialog',
  ],
  contexts: {
    'declare-dialog':
      'Choose an insurer in the Insurer filter, tick at least one payment, then click "Declare payments" in the toolbar.',
    'declare-result': 'In the "Declare payments" dialog, click the "Declare payments" button at the bottom.',
    'report-ready': 'In the "Declare payments" dialog, click "Generate report" at the bottom.',
    'invalidate-dialog':
      'Tick one or more payments, then click the invalidate icon in the last column of a ticked row.',
  },
  tips: [
    'Changing any filter (dates, insurer, client, policy number, segment, currency, payment type, declared toggle or GrECo companies) loads the list again from the server. The search box only narrows the rows that are already loaded.',
    'Declared payments are hidden by default. A person icon in the narrow column next to the tick box marks a declared payment; hover it to see who declared it, when, and whether it was declared manually.',
    'A red "Declared premium" means the amount declared to the insurer differs from the premium that was cleared. Red insurer report columns mean the linked insurer report states a different premium, commission or commission rate than PCT.',
    'Generating the insurer Excel report also marks every payment in it as declared, exactly like a manual declaration. Generate it only when you really send the report to the insurer.',
    'Where this page sits: bank statements are cleared on the Bank statements page (or money is allocated from Client balance), the cleared payments appear here, and declaring them to the insurer is the last step.',
  ],
  steps: [
    // Getting around
    {
      selector: '[data-help="payments-page-switcher"]',
      title: 'Switch to another PCT page',
      body: 'The icons jump between Bank statements, Insurer reports, Client balance, Payment plans, Reminders and Payments; hover an icon to see its name. The highlighted icon is the page you are on.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pereiti į kitą PCT puslapį',
          body: 'Piktogramos perjungia „Banko išrašai“, draudikų ataskaitų, „Kliento balansas“, mokėjimų planų, priminimų ir „Mokėjimai“ puslapius; užvedę pelę ant piktogramos pamatysite jos pavadinimą. Paryškinta piktograma rodo, kuriame puslapyje esate.',
        },
        lv: {
          title: 'Pāriet uz citu PCT lapu',
          body: 'Ikonas pārslēdz lapas "Bankas konta izraksti", apdrošinātāju atskaites, "Client balance", maksājumu plānus, atgādinājumus un "Payments"; novietojiet peli virs ikonas, lai redzētu tās nosaukumu. Izceltā ikona rāda lapu, kurā atrodaties.',
        },
      },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: 'Download the user manual',
      body: 'Downloads the complete PCT user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Lithuanian or Latvian when the app is in that language, English otherwise. The page guide offers the same download (the book next to EN / LT / LV) in the guide’s language.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atsisiųsti naudotojo vadovą',
          body: 'Atsisiunčia visą PCT naudotojo vadovą PDF formatu su visų puslapių ekrano kopijomis. Gaunate jį kalba, pasirinkta „Nustatymuose“: lietuvių arba latvių, kai programa veikia ta kalba, kitu atveju – anglų. Puslapio vadove tas pats atsisiuntimas (knygos piktograma šalia EN / LT / LV) pateikiamas vadovo kalba.',
        },
        lv: {
          title: 'Lejupielādēt lietotāja rokasgrāmatu',
          body: 'Lejupielādē pilnu PCT lietotāja rokasgrāmatu PDF formātā ar visu lapu ekrānuzņēmumiem. Jūs to saņemat valodā, kas izvēlēta sadaļā “Settings”: latviešu vai lietuviešu, ja lietotne darbojas šajā valodā, citādi – angļu. Lapas ceļvedī tā pati lejupielāde (grāmatas ikona blakus EN / LT / LV) ir pieejama ceļveža valodā.',
        },
      },
    },
    {
      selector: '[data-help="payments-refresh"]',
      title: 'Reload the payment list',
      body: 'Loads the payments again from the server with the current filters. Use it after someone else cleared or declared payments, or after you worked on another page; ticked rows are cleared by the reload.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atnaujinti mokėjimų sąrašą',
          body: 'Iš naujo įkelia mokėjimus iš serverio pagal esamus filtrus. Naudokite, kai kas nors kitas užskaitė ar deklaravo mokėjimus arba kai dirbote kitame puslapyje; atnaujinus pažymėtos eilutės nuimamos.',
        },
        lv: {
          title: 'Pārlādēt maksājumu sarakstu',
          body: 'No servera vēlreiz ielādē maksājumus ar pašreizējiem filtriem. Izmantojiet to, ja kāds cits ir piesaistījis vai deklarējis maksājumus vai ja strādājāt citā lapā; pēc pārlādes atzīmētās rindas tiek noņemtas.',
        },
      },
    },
    {
      selector: '[data-help="payments-companies"]',
      title: 'Choose the GrECo companies',
      body: 'Tick the GrECo companies whose payments you want to see; at least one must stay ticked. The list reloads when the menu closes. Declaration always runs for one company, so with several ticked the "Declare payments" button asks which one.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pasirinkti GrECo įmones',
          body: 'Pažymėkite GrECo įmones, kurių mokėjimus norite matyti; bent viena turi likti pažymėta. Sąrašas atnaujinamas uždarius meniu. Deklaruojama visada vienai įmonei, todėl pažymėjus kelias mygtukas „Deklaruoti įmokas“ paklaus, kuriai.',
        },
        lv: {
          title: 'Izvēlēties GrECo uzņēmumus',
          body: 'Atzīmējiet GrECo uzņēmumus, kuru maksājumus vēlaties redzēt; vismaz vienam jāpaliek atzīmētam. Saraksts tiek pārlādēts, kad izvēlne aizveras. Deklarēšana vienmēr notiek vienam uzņēmumam, tāpēc, ja atzīmēti vairāki, poga "Declare payments" pajautās, kuram.',
        },
      },
    },
    {
      selector: '[data-help="payments-export-excel"]',
      title: 'Export the list to Excel',
      body: 'Downloads every loaded payment with all grid columns as an .xlsx file; cells shown in red in the grid are marked in the file too. The search box is ignored, so narrow the list with the filters first if you need only part of it.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Eksportuoti sąrašą į Excel',
          body: 'Mygtukas „Export Excel“ atsisiunčia visus įkeltus mokėjimus su visais lentelės stulpeliais kaip .xlsx failą; lentelėje raudonai rodomi langeliai pažymimi ir faile. Paieškos laukas neįvertinamas, todėl, jei reikia tik dalies, pirmiausia susiaurinkite sąrašą filtrais.',
        },
        lv: {
          title: 'Eksportēt sarakstu uz Excel',
          body: 'Poga "Export Excel" lejupielādē visus ielādētos maksājumus ar visām tabulas kolonnām kā .xlsx failu; tabulā sarkanās šūnas tiek atzīmētas arī failā. Meklēšanas lauks netiek ņemts vērā, tāpēc, ja vajag tikai daļu, vispirms sašauriniet sarakstu ar filtriem.',
        },
      },
    },
    // Finding payments
    {
      selector: '[data-help="payments-date-from"]',
      title: 'Set the start of the period',
      body: 'First day of the period, inclusive. The page opens with the last month up to today. Type the date as day.month.year or pick it from the calendar; which date is compared is set by the date-type list next to the two dates.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Nustatyti laikotarpio pradžią',
          body: 'Pirmoji laikotarpio diena, įskaitytinai. Atidarius puslapį rodomas paskutinis mėnuo iki šiandien. Įveskite datą formatu diena.mėnuo.metai arba pasirinkite kalendoriuje; kuri data lyginama, nustato datos tipo sąrašas šalia abiejų datų.',
        },
        lv: {
          title: 'Iestatīt perioda sākumu',
          body: 'Perioda pirmā diena, ieskaitot. Atverot lapu, tiek rādīts pēdējais mēnesis līdz šodienai. Ievadiet datumu formātā diena.mēnesis.gads vai izvēlieties kalendārā; kurš datums tiek salīdzināts, nosaka datuma veida saraksts blakus abiem datumiem.',
        },
      },
    },
    {
      selector: '[data-help="payments-date-to"]',
      title: 'Set the end of the period',
      body: 'Last day of the period; payments on this day are included. Changing either date reloads the list straight away, so set the start date first when you move to a different month.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Nustatyti laikotarpio pabaigą',
          body: 'Paskutinė laikotarpio diena; šios dienos mokėjimai įtraukiami. Pakeitus bet kurią datą sąrašas iš karto įkeliamas iš naujo, todėl pereidami į kitą mėnesį pirmiausia nustatykite pradžios datą.',
        },
        lv: {
          title: 'Iestatīt perioda beigas',
          body: 'Perioda pēdējā diena; šīs dienas maksājumi tiek iekļauti. Mainot jebkuru datumu, saraksts uzreiz tiek pārlādēts, tāpēc, pārejot uz citu mēnesi, vispirms iestatiet sākuma datumu.',
        },
      },
    },
    {
      selector: '[data-help="payments-date-field"]',
      title: 'Choose which date the period filters on',
      body: 'Payment date (default), Cleared date (when the payment was cleared in PCT), Booking date (bank booking), Declared date or Insurer report date. To find what was declared last week, pick Declared date and switch on declared payments with the toggle next to "Declare payments".',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Pasirinkti, pagal kurią datą filtruojamas laikotarpis',
          body: '„Mokėjimo data“ (numatytoji), „Užskaityta“ (kada mokėjimas užskaitytas PCT), „Įmokos data“ (banko įrašo data), „Deklaruota“ arba „Draudiko ataskaitos data“. Norėdami rasti praėjusią savaitę deklaruotus mokėjimus, pasirinkite „Deklaruota“ ir jungikliu šalia „Deklaruoti įmokas“ įtraukite deklaruotus mokėjimus.',
        },
        lv: {
          title: 'Izvēlēties, pēc kura datuma filtrēt periodu',
          body: '"Payment date" (noklusējums), "Cleared date" (kad maksājums piesaistīts PCT), "Booking date" (bankas grāmatojums), "Declared date" vai "Apdrošinātāja atskaites datums". Lai atrastu pagājušajā nedēļā deklarēto, izvēlieties "Declared date" un ar slēdzi blakus "Declare payments" ieslēdziet deklarētos maksājumus.',
        },
      },
    },
    {
      selector: '[data-help="payments-insurer"]',
      title: 'Filter by insurer',
      body: 'Type part of the insurer name and pick it from the list. Choosing an insurer is required before you can declare payments or generate the insurer report, because a declaration always goes to exactly one insurer.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Filtruoti pagal draudiką',
          body: 'Lauke „Pasirinkti draudiką“ įveskite draudiko pavadinimo dalį ir pasirinkite iš sąrašo. Draudiką būtina pasirinkti prieš deklaruojant mokėjimus ar generuojant draudiko ataskaitą, nes deklaracija visada siunčiama vienam draudikui.',
        },
        lv: {
          title: 'Filtrēt pēc apdrošinātāja',
          body: 'Laukā "Search insurers" ievadiet daļu no apdrošinātāja nosaukuma un izvēlieties to sarakstā. Apdrošinātājs jāizvēlas, pirms var deklarēt maksājumus vai ģenerēt apdrošinātāja atskaiti, jo deklarācija vienmēr tiek sūtīta tieši vienam apdrošinātājam.',
        },
      },
    },
    {
      selector: '[data-help="payments-client"]',
      title: 'Filter by client',
      body: 'Type part of the client name and pick the client to see only that client\'s payments. Clear the field with the small cross to see all clients again.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Filtruoti pagal klientą',
          body: 'Lauke „Pasirinkti klientą“ įveskite kliento pavadinimo dalį ir pasirinkite klientą – bus rodomi tik jo mokėjimai. Norėdami vėl matyti visus klientus, išvalykite lauką mažu kryželiu.',
        },
        lv: {
          title: 'Filtrēt pēc klienta',
          body: 'Laukā "Select client" ievadiet daļu no klienta nosaukuma un izvēlieties klientu, lai redzētu tikai viņa maksājumus. Lai atkal redzētu visus klientus, notīriet lauku ar mazo krustiņu.',
        },
      },
    },
    {
      selector: '[data-help="payments-policy-number"]',
      title: 'Filter by policy number',
      body: 'Enter the complete policy number; the server looks for an exact match, so part of a number finds nothing. Use the search box instead when you only know part of it.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Filtruoti pagal poliso numerį',
          body: 'Lauke „Poliso numeris“ įveskite visą poliso numerį; serveris ieško tikslaus atitikmens, todėl pagal numerio dalį nieko nerasite. Jei žinote tik dalį numerio, naudokite paieškos lauką.',
        },
        lv: {
          title: 'Filtrēt pēc polises numura',
          body: 'Laukā "Policy Number" ievadiet pilnu polises numuru; serveris meklē precīzu atbilstību, tāpēc pēc numura daļas nekas netiks atrasts. Ja zināt tikai daļu, izmantojiet meklēšanas lauku.',
        },
      },
    },
    {
      selector: '[data-help="payments-service-segment"]',
      title: 'Filter by service segment',
      body: 'Restricts the list to one service segment. "All service segments" shows every segment.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Filtruoti pagal draudimo liniją',
          body: 'Rodomi tik vienos draudimo linijos (paslaugų segmento) mokėjimai. Pasirinkus „Visos draudimo linijos“ rodomos visos.',
        },
        lv: {
          title: 'Filtrēt pēc pakalpojumu segmenta',
          body: 'Sarakstā paliek tikai viena pakalpojumu segmenta maksājumi. Izvēlnes vienums "All service segments" rāda visus segmentus.',
        },
      },
    },
    {
      selector: '[data-help="payments-currency"]',
      title: 'Choose the currency',
      body: 'Only payments in this currency are listed; one currency is always selected. The declaration dialog shows its totals in the same currency, so set it before you declare.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Pasirinkti valiutą',
          body: 'Rodomi tik šios valiutos mokėjimai; visada pasirinkta viena valiuta. Deklaravimo lange sumos rodomos ta pačia valiuta, todėl nustatykite ją prieš deklaruodami.',
        },
        lv: {
          title: 'Izvēlēties valūtu',
          body: 'Sarakstā ir tikai maksājumi šajā valūtā; vienmēr ir izvēlēta viena valūta. Deklarēšanas logā summas tiek rādītas tajā pašā valūtā, tāpēc iestatiet to pirms deklarēšanas.',
        },
      },
    },
    {
      selector: '[data-help="payments-discount-filter"]',
      title: 'Show premium or discount payments',
      body: '"All payments", "Discount payments" (payments of a discount, marked DSC in the grid) or "Premium payments" (ordinary premium). The choice is also applied when you declare or generate the insurer report.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Rodyti įmokų arba nuolaidų mokėjimus',
          body: '„Visi mokėjimai“, „Discount payments“ (nuolaidos mokėjimai, lentelėje pažymėti DSC) arba „Premium payments“ (įprastos įmokos). Šis pasirinkimas taikomas ir deklaruojant bei generuojant draudiko ataskaitą.',
        },
        lv: {
          title: 'Rādīt prēmiju vai atlaižu maksājumus',
          body: '"All payments", "Discount payments" (atlaižu maksājumi, tabulā atzīmēti ar DSC) vai "Premium payments" (parastās prēmijas). Šī izvēle tiek piemērota arī deklarējot un ģenerējot apdrošinātāja atskaiti.',
        },
      },
    },
    {
      selector: '[data-help="payments-exclude-declared"]',
      title: 'Show or hide declared payments',
      body: 'Pressed (the default) hides payments already declared through the insurer API or manually, and payments already sent in an insurer Excel report. Exception: report-only payments of insurers that also accept API declaration stay visible so they can still be declared. Click to include declared payments, for example to check what was sent.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Rodyti arba slėpti deklaruotus mokėjimus',
          body: 'Paspaustas (numatytoji būsena) slepia mokėjimus, jau deklaruotus per draudiko API arba rankiniu būdu, ir jau išsiųstus draudiko Excel ataskaitoje. Išimtis: tik ataskaitoje buvę draudikų, priimančių API deklaravimą, mokėjimai lieka matomi, kad juos dar būtų galima deklaruoti. Spustelėkite, jei norite įtraukti deklaruotus mokėjimus, pvz., patikrinti, kas išsiųsta.',
        },
        lv: {
          title: 'Rādīt vai slēpt deklarētos maksājumus',
          body: 'Nospiests (noklusējums) slēpj maksājumus, kas jau deklarēti caur apdrošinātāja API vai manuāli, un tos, kas jau nosūtīti apdrošinātāja Excel atskaitē. Izņēmums: tikai atskaitē iekļautie maksājumi apdrošinātājiem, kas pieņem API deklarēšanu, paliek redzami, lai tos vēl varētu deklarēt. Noklikšķiniet, lai iekļautu deklarētos maksājumus, piemēram, lai pārbaudītu nosūtīto.',
        },
      },
    },
    {
      selector: '[data-help="payments-search"]',
      title: 'Search within the loaded payments',
      body: 'Filters the rows already on screen by insurer, client, policy name, policy number or sub-number, reminder number, comment, amounts or dates as you type. It does not ask the server again, so a payment outside the current filters is never found here.',
      section: 'Finding payments',
      i18n: {
        lt: {
          title: 'Ieškoti tarp įkeltų mokėjimų',
          body: 'Laukas „Search“ rašant filtruoja jau rodomas eilutes pagal draudiką, klientą, poliso pavadinimą, poliso numerį ar subnumerį, priminimo numerį, komentarą, sumas ar datas. Serveris iš naujo neužklausiamas, todėl už esamų filtrų esantis mokėjimas čia nebus rastas.',
        },
        lv: {
          title: 'Meklēt ielādētajos maksājumos',
          body: 'Lauks "Search" rakstīšanas laikā filtrē jau redzamās rindas pēc apdrošinātāja, klienta, polises nosaukuma, polises numura vai apakšnumura, atgādinājuma numura, komentāra, summām vai datumiem. Serveris netiek vaicāts vēlreiz, tāpēc maksājums ārpus pašreizējiem filtriem šeit netiks atrasts.',
        },
      },
    },
    // Reading the list
    {
      selector: '[data-help="payments-results-count"]',
      title: 'Check how many payments match',
      body: 'The number of rows currently shown, after the search box. If it is zero, widen the period, clear the insurer or client, or switch on declared payments.',
      section: 'Reading the list',
      i18n: {
        lt: {
          title: 'Patikrinti, kiek mokėjimų atitinka',
          body: '„Paieškos rezultatai“ rodo šiuo metu matomų eilučių skaičių, įvertinus paieškos lauką. Jei jis lygus nuliui, praplėskite laikotarpį, išvalykite draudiką ar klientą arba įtraukite deklaruotus mokėjimus.',
        },
        lv: {
          title: 'Pārbaudīt, cik maksājumu atbilst',
          body: '"Search results" rāda pašlaik redzamo rindu skaitu pēc meklēšanas lauka. Ja tas ir nulle, paplašiniet periodu, notīriet apdrošinātāju vai klientu vai ieslēdziet deklarētos maksājumus.',
        },
      },
    },
    {
      selector: '[data-help="payments-grid"]',
      title: 'Read and select payments in the grid',
      body: 'One row per cleared payment. Tick the box at the start of a row to select it for declaring or invalidating; the header box ticks all rows. DSC marks a discount payment, the person icon a declared one. Premium, Declared premium and the action column stay pinned on the right; the insurer report columns show what the insurer reported back. Click a column header to sort.',
      section: 'Reading the list',
      i18n: {
        lt: {
          title: 'Peržiūrėti ir pažymėti mokėjimus lentelėje',
          body: 'Viena eilutė – vienas užskaitytas mokėjimas. Pažymėkite langelį eilutės pradžioje, kad pasirinktumėte ją deklaravimui ar panaikinimui; antraštės langelis pažymi visas eilutes. DSC žymi nuolaidos mokėjimą, žmogaus piktograma – deklaruotą. „Įmokos dydis“, „Declared premium“ ir veiksmų stulpelis visada lieka dešinėje; draudiko ataskaitos stulpeliai rodo, ką draudikas pateikė. Spustelėkite stulpelio antraštę, kad rūšiuotumėte.',
        },
        lv: {
          title: 'Skatīt un atlasīt maksājumus tabulā',
          body: 'Viena rinda ir viens piesaistīts maksājums. Atzīmējiet rūtiņu rindas sākumā, lai to atlasītu deklarēšanai vai anulēšanai; virsraksta rūtiņa atzīmē visas rindas. DSC apzīmē atlaides maksājumu, cilvēka ikona – deklarētu. "Premium", "Declared premium" un darbību kolonna vienmēr paliek labajā pusē; apdrošinātāja atskaites kolonnas rāda, ko apdrošinātājs ir paziņojis. Noklikšķiniet uz kolonnas virsraksta, lai kārtotu.',
        },
      },
    },
    {
      selector: '[data-help="payments-row-invalidate"]',
      title: 'Invalidate the ticked payments',
      body: 'The icon appears in the last column of every ticked row and acts on all ticked rows at once, not only on its own row. It opens a confirmation dialog; nothing changes until you confirm there.',
      section: 'Reading the list',
      optional: true,
      i18n: {
        lt: {
          title: 'Panaikinti pažymėtus mokėjimus',
          body: 'Piktograma „Panaikinti mokėjimą“ atsiranda kiekvienos pažymėtos eilutės paskutiniame stulpelyje ir veikia visas pažymėtas eilutes iš karto, ne tik savo eilutę. Ji atidaro patvirtinimo langą; niekas nepasikeičia, kol ten nepatvirtinate.',
        },
        lv: {
          title: 'Anulēt atzīmētos maksājumus',
          body: 'Ikona "Invalidate payment" parādās katras atzīmētās rindas pēdējā kolonnā un iedarbojas uz visām atzīmētajām rindām reizē, ne tikai uz savu rindu. Tā atver apstiprinājuma logu; nekas nemainās, kamēr tur neapstiprināt.',
        },
      },
    },
    // Declaring to the insurer
    {
      selector: '[data-help="payments-declare-mode"]',
      title: 'Pick automatic or manual declaration',
      body: 'The small button inside "Declare payments" switches the mode before you open the dialog. Robot icon = automatic: the payments are sent to the insurer through its API. Hand icon = manual: PCT only records that you declared them outside PCT. Both work only for the Sagauta company; for other GrECo companies use the insurer report.',
      section: 'Declaring to the insurer',
      i18n: {
        lt: {
          title: 'Pasirinkti automatinį arba rankinį deklaravimą',
          body: 'Mažas mygtukas mygtuko „Deklaruoti įmokas“ viduje perjungia režimą prieš atidarant langą. Roboto piktograma – „Automatic payment declaration“: mokėjimai siunčiami draudikui per jo API. Rankos piktograma – „Manual payment declaration“: PCT tik užfiksuoja, kad juos deklaravote ne PCT. Abu režimai veikia tik įmonei Sagauta; kitoms GrECo įmonėms naudokite draudiko ataskaitą.',
        },
        lv: {
          title: 'Izvēlēties automātisku vai manuālu deklarēšanu',
          body: 'Mazā poga pogas "Declare payments" iekšpusē pārslēdz režīmu pirms loga atvēršanas. Robota ikona – "Automatic payment declaration": maksājumi tiek nosūtīti apdrošinātājam caur tā API. Rokas ikona – "Manual payment declaration": PCT tikai reģistrē, ka tos deklarējāt ārpus PCT. Abi režīmi darbojas tikai uzņēmumam Sagauta; citiem GrECo uzņēmumiem izmantojiet apdrošinātāja atskaiti.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-button"]',
      title: 'Open the declaration for the ticked payments',
      body: 'Opens the "Declare payments" dialog with the ticked rows. It stays disabled until an insurer is chosen in the Insurer filter and at least one row is ticked. With several GrECo companies selected it first asks which company you declare for.',
      section: 'Declaring to the insurer',
      i18n: {
        lt: {
          title: 'Atidaryti pažymėtų mokėjimų deklaravimą',
          body: 'Mygtukas „Deklaruoti įmokas“ atidaro deklaravimo langą su pažymėtomis eilutėmis. Jis neaktyvus, kol filtre „Draudikas“ nepasirinktas draudikas ir nepažymėta bent viena eilutė. Jei pasirinktos kelios GrECo įmonės, pirmiausia paklausiama, kuriai įmonei deklaruojate.',
        },
        lv: {
          title: 'Atvērt atzīmēto maksājumu deklarēšanu',
          body: 'Poga "Declare payments" atver deklarēšanas logu ar atzīmētajām rindām. Tā ir neaktīva, kamēr filtrā "Insurer" nav izvēlēts apdrošinātājs un nav atzīmēta vismaz viena rinda. Ja izvēlēti vairāki GrECo uzņēmumi, vispirms tiek jautāts, kuram uzņēmumam deklarējat.',
        },
      },
    },
    // Declare payments dialog
    {
      selector: '[data-help="payments-declare-dialog"]',
      title: 'Review what will be declared',
      body: 'The title shows how many payments go to which insurer, prefixed with "Manual payment declaration" in manual mode. Nothing is sent or saved until you click "Generate report" or "Declare payments"; "Close" discards the dialog.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      i18n: {
        lt: {
          title: 'Peržiūrėti, kas bus deklaruota',
          body: 'Pavadinime matyti, kiek mokėjimų ir kuriam draudikui deklaruojama; rankiniame režime pridedama „Manual payment declaration“. Niekas neišsiunčiama ir neišsaugoma, kol nespustelėsite „Generate report“ arba „Deklaruoti įmokas“; „Uždaryti“ langą uždaro be pakeitimų.',
        },
        lv: {
          title: 'Pārskatīt, kas tiks deklarēts',
          body: 'Virsrakstā redzams, cik maksājumu un kuram apdrošinātājam tiek deklarēts; manuālajā režīmā priekšā ir "Manual payment declaration". Nekas netiek nosūtīts vai saglabāts, kamēr nenoklikšķināt "Generate report" vai "Declare payments"; "Close" aizver logu bez izmaiņām.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-grid"]',
      title: 'Adjust the amount to declare per payment',
      body: 'The "To be declared" column starts with the premium (or the amount declared before, if any). Click a cell and type a different amount, for example to declare only part of a payment; a minus sign is ignored. The amount you enter is what the insurer receives and what is stored as declared premium.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      i18n: {
        lt: {
          title: 'Pakoreguoti deklaruojamą sumą kiekvienam mokėjimui',
          body: 'Stulpelyje „To be declared“ iš pradžių rodoma įmoka (arba anksčiau deklaruota suma, jei tokia yra). Spustelėkite langelį ir įveskite kitą sumą, pvz., jei deklaruojate tik dalį mokėjimo; minuso ženklas ignoruojamas. Įvesta suma siunčiama draudikui ir išsaugoma kaip deklaruota įmoka.',
        },
        lv: {
          title: 'Pielāgot deklarējamo summu katram maksājumam',
          body: 'Kolonnā "To be declared" sākumā ir prēmija (vai iepriekš deklarētā summa, ja tāda ir). Noklikšķiniet uz šūnas un ievadiet citu summu, piemēram, lai deklarētu tikai daļu maksājuma; mīnusa zīme tiek ignorēta. Ievadītā summa tiek nosūtīta apdrošinātājam un saglabāta kā deklarētā prēmija.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-fill-all"]',
      title: 'Fill every row with its full premium',
      body: 'The lightning icon in the "To be declared" header sets every row back to its full premium. Use it after experimenting with amounts to start again from the defaults.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      i18n: {
        lt: {
          title: 'Užpildyti visas eilutes visa įmoka',
          body: 'Žaibo piktograma („Pasirinkti viską“) stulpelio „To be declared“ antraštėje kiekvienai eilutei grąžina visą įmokos sumą. Naudokite ją, jei norite pradėti iš naujo nuo numatytųjų sumų.',
        },
        lv: {
          title: 'Aizpildīt visas rindas ar pilnu prēmiju',
          body: 'Zibens ikona ("Select all") kolonnas "To be declared" virsrakstā katrai rindai atjauno pilnu prēmijas summu. Izmantojiet to, lai sāktu no jauna ar noklusējuma summām.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-clear-all"]',
      title: 'Set every row to zero',
      body: 'The cross icon in the "To be declared" header sets all amounts to 0. Useful when only a few payments should be declared: clear all, then fill just those rows.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      i18n: {
        lt: {
          title: 'Nustatyti visas eilutes į nulį',
          body: 'Kryželio piktograma („Clear all“) stulpelio „To be declared“ antraštėje visas sumas nustato į 0. Patogu, kai reikia deklaruoti tik kelis mokėjimus: išvalykite viską ir užpildykite tik tas eilutes.',
        },
        lv: {
          title: 'Iestatīt visas rindas uz nulli',
          body: 'Krustiņa ikona ("Clear all") kolonnas "To be declared" virsrakstā visas summas iestata uz 0. Noder, ja jādeklarē tikai daži maksājumi: notīriet visu un aizpildiet tikai šīs rindas.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-row-fill"]',
      title: 'Fill one row with the remaining amount',
      body: 'Sets this row to what is still missing to reach the total premium, but never more than the row\'s own premium. Ctrl+click marks several rows and Shift+click marks a range; a plain click on a marked row then fills all marked rows.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Užpildyti eilutę likusia suma',
          body: 'Eilutei nustatoma suma, kurios dar trūksta iki bendros įmokų sumos, bet ne didesnė už pačios eilutės įmoką. Ctrl+spustelėjimas pažymi kelias eilutes, Shift+spustelėjimas – intervalą; paprastas spustelėjimas ant pažymėtos eilutės užpildo visas pažymėtas.',
        },
        lv: {
          title: 'Aizpildīt rindu ar atlikušo summu',
          body: 'Rindai tiek iestatīts tas, kas vēl trūkst līdz kopējai prēmijai, bet ne vairāk kā pašas rindas prēmija. Ctrl+klikšķis atzīmē vairākas rindas, Shift+klikšķis – diapazonu; parasts klikšķis uz atzīmētas rindas aizpilda visas atzīmētās.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-row-zero"]',
      title: 'Exclude one row from the declaration amount',
      body: 'Sets this row to 0. Ctrl+click and Shift+click mark several rows the same way as the fill icon; a plain click on a marked row then zeroes all marked rows.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Nustatyti vienos eilutės sumą į nulį',
          body: 'Eilutės suma nustatoma į 0. Ctrl+spustelėjimu ir Shift+spustelėjimu galima pažymėti kelias eilutes taip pat kaip su užpildymo piktograma; paprastas spustelėjimas ant pažymėtos eilutės visas pažymėtas nustato į nulį.',
        },
        lv: {
          title: 'Iestatīt vienas rindas summu uz nulli',
          body: 'Rindas summa tiek iestatīta uz 0. Ar Ctrl+klikšķi un Shift+klikšķi var atzīmēt vairākas rindas tāpat kā ar aizpildīšanas ikonu; parasts klikšķis uz atzīmētas rindas visas atzīmētās iestata uz nulli.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-total"]',
      title: 'Compare the declared total with the premium total',
      body: '"To be declared" shows the sum you are about to declare against the total premium of the selected payments, in the filter currency. It turns red when you declare more than the premium. If the selected premium total is negative, "Generate report" and "Declare payments" stay disabled.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      i18n: {
        lt: {
          title: 'Palyginti deklaruojamą sumą su įmokų suma',
          body: '„To be declared“ rodo sumą, kurią ketinate deklaruoti, ir bendrą pasirinktų mokėjimų įmokų sumą filtro valiuta. Ji tampa raudona, jei deklaruojate daugiau nei įmoka. Jei pasirinktų įmokų suma neigiama, „Generate report“ ir „Deklaruoti įmokas“ lieka neaktyvūs.',
        },
        lv: {
          title: 'Salīdzināt deklarējamo summu ar prēmiju kopsummu',
          body: '"To be declared" rāda summu, ko gatavojaties deklarēt, pret atlasīto maksājumu kopējo prēmiju filtra valūtā. Tā kļūst sarkana, ja deklarējat vairāk par prēmiju. Ja atlasīto prēmiju kopsumma ir negatīva, "Generate report" un "Declare payments" paliek neaktīvas.',
        },
      },
    },
    {
      selector: '[data-help="payments-generate-report"]',
      title: 'Generate the insurer Excel report',
      body: 'Builds the insurer-specific Excel report from the payments and amounts in the dialog and marks those payments as declared. It works for insurers that have a report template (several Lithuanian insurers, and any insurer for GrECo Latvia); otherwise the error says the excel format is not defined for the chosen insurer.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      i18n: {
        lt: {
          title: 'Sugeneruoti draudiko Excel ataskaitą',
          body: '„Generate report“ iš lango mokėjimų ir sumų sukuria konkrečiam draudikui skirtą Excel ataskaitą ir pažymi tuos mokėjimus kaip deklaruotus. Veikia draudikams, turintiems ataskaitos šabloną (keliems Lietuvos draudikams ir bet kuriam GrECo Latvijos draudikui); kitu atveju klaida praneša, kad pasirinktam draudikui Excel formatas neapibrėžtas.',
        },
        lv: {
          title: 'Ģenerēt apdrošinātāja Excel atskaiti',
          body: '"Generate report" no loga maksājumiem un summām izveido konkrētajam apdrošinātājam paredzētu Excel atskaiti un atzīmē šos maksājumus kā deklarētus. Darbojas apdrošinātājiem, kuriem ir atskaites veidne (vairākiem Lietuvas apdrošinātājiem un jebkuram GrECo Latvijas apdrošinātājam); citādi kļūda ziņo, ka izvēlētajam apdrošinātājam Excel formāts nav definēts.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-submit"]',
      title: 'Declare the payments',
      body: 'Sends the declaration in the chosen mode. Automatic mode posts the payments to the insurer API and marks as declared only the lines the insurer accepted; manual mode just marks them declared and never overwrites a payment already declared through the API. The server answer then appears in the dialog and the list reloads.',
      section: 'Declare payments dialog',
      context: 'declare-dialog',
      i18n: {
        lt: {
          title: 'Deklaruoti mokėjimus',
          body: '„Deklaruoti įmokas“ išsiunčia deklaraciją pasirinktu režimu. Automatinis režimas siunčia mokėjimus į draudiko API ir deklaruotais pažymi tik draudiko priimtas eilutes; rankinis režimas tik pažymi juos deklaruotais ir niekada neperrašo per API jau deklaruoto mokėjimo. Tada lange parodomas serverio atsakymas, o sąrašas atnaujinamas.',
        },
        lv: {
          title: 'Deklarēt maksājumus',
          body: '"Declare payments" nosūta deklarāciju izvēlētajā režīmā. Automātiskais režīms nosūta maksājumus uz apdrošinātāja API un par deklarētām atzīmē tikai apdrošinātāja pieņemtās rindas; manuālais režīms tikai atzīmē tos kā deklarētus un nekad nepārraksta maksājumu, kas jau deklarēts caur API. Pēc tam logā parādās servera atbilde, un saraksts tiek pārlādēts.',
        },
      },
    },
    // After declaring
    {
      selector: '[data-help="payments-declare-response"]',
      title: 'Read the declaration response',
      body: 'Shows what the server answered: the insurer response per line, or a message such as "Payment declaration not implemented for company" or "There is no API implementation for insurer". In those two cases nothing was marked as declared.',
      section: 'After declaring',
      context: 'declare-result',
      i18n: {
        lt: {
          title: 'Perskaityti deklaravimo atsakymą',
          body: 'Rodo serverio atsakymą: draudiko atsakymą kiekvienai eilutei arba pranešimą, pvz., „Payment declaration not implemented for company“ ar „There is no API implementation for insurer“. Šiais dviem atvejais niekas nebuvo pažymėta kaip deklaruota.',
        },
        lv: {
          title: 'Izlasīt deklarēšanas atbildi',
          body: 'Rāda servera atbildi: apdrošinātāja atbildi katrai rindai vai ziņojumu, piemēram, "Payment declaration not implemented for company" vai "There is no API implementation for insurer". Šajos divos gadījumos nekas netika atzīmēts kā deklarēts.',
        },
      },
    },
    {
      selector: '[data-help="payments-declare-copy"]',
      title: 'Copy the response',
      body: '"Copy to Clipboard" copies the whole response text, for example to send it to IT or keep it with the insurer correspondence. The dialog stays open; close it with "Close".',
      section: 'After declaring',
      context: 'declare-result',
      i18n: {
        lt: {
          title: 'Nukopijuoti atsakymą',
          body: '„Copy to Clipboard“ nukopijuoja visą atsakymo tekstą, pvz., norint jį persiųsti IT arba saugoti kartu su susirašinėjimu su draudiku. Langas lieka atidarytas; uždarykite jį mygtuku „Uždaryti“.',
        },
        lv: {
          title: 'Kopēt atbildi',
          body: '"Copy to Clipboard" nokopē visu atbildes tekstu, piemēram, lai to nosūtītu IT vai saglabātu kopā ar saraksti ar apdrošinātāju. Logs paliek atvērts; aizveriet to ar "Close".',
        },
      },
    },
    {
      selector: '[data-help="payments-download-report"]',
      title: 'Download the generated report',
      body: 'Opens the generated Excel file in a new browser tab and closes the dialog. The payments in it are already marked as declared at this point, so with declared payments hidden they leave the list, except for insurers that also accept API declaration.',
      section: 'After declaring',
      context: 'report-ready',
      i18n: {
        lt: {
          title: 'Atsisiųsti sugeneruotą ataskaitą',
          body: '„Download report“ atidaro sugeneruotą Excel failą naujame naršyklės skirtuke ir uždaro langą. Jame esantys mokėjimai jau pažymėti kaip deklaruoti, todėl, kol deklaruoti mokėjimai slepiami, jie dingsta iš sąrašo, išskyrus draudikus, kurie priima ir API deklaravimą.',
        },
        lv: {
          title: 'Lejupielādēt ģenerēto atskaiti',
          body: '"Download report" atver ģenerēto Excel failu jaunā pārlūka cilnē un aizver logu. Tajā iekļautie maksājumi jau ir atzīmēti kā deklarēti, tāpēc, kamēr deklarētie maksājumi ir paslēpti, tie pazūd no saraksta, izņemot apdrošinātājus, kas pieņem arī API deklarēšanu.',
        },
      },
    },
    // Invalidate payments dialog
    {
      selector: '[data-help="payments-invalidate-dialog"]',
      title: 'Confirm which payments are invalidated',
      body: 'Asks you to confirm invalidating the number of ticked payments. Invalidation is for a payment cleared to the wrong client, policy or amount: the payment disappears from this list and, if it was cleared from a bank statement, the money goes back to the client balance so it can be cleared again.',
      section: 'Invalidate payments dialog',
      context: 'invalidate-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti, kurie mokėjimai panaikinami',
          body: 'Prašoma patvirtinti pažymėtų mokėjimų panaikinimą. Panaikinimas skirtas mokėjimui, užskaitytam netinkamam klientui, polisui ar netinkama suma: mokėjimas dingsta iš šio sąrašo, o jei jis buvo užskaitytas iš banko išrašo, pinigai grįžta į kliento balansą, kad juos būtų galima užskaityti iš naujo.',
        },
        lv: {
          title: 'Apstiprināt, kuri maksājumi tiek anulēti',
          body: 'Tiek lūgts apstiprināt atzīmēto maksājumu anulēšanu. Anulēšana ir paredzēta maksājumam, kas piesaistīts nepareizam klientam, polisei vai ar nepareizu summu: maksājums pazūd no šī saraksta, un, ja tas bija piesaistīts no bankas konta izraksta, nauda atgriežas klienta bilancē, lai to varētu piesaistīt no jauna.',
        },
      },
    },
    {
      selector: '[data-help="payments-invalidate-comment"]',
      title: 'Explain why the payment is invalidated',
      body: 'Optional, but recommended. The text is appended to each payment comment (the comment is cut at 512 characters), so colleagues can later see why it was reversed.',
      section: 'Invalidate payments dialog',
      context: 'invalidate-dialog',
      i18n: {
        lt: {
          title: 'Nurodyti panaikinimo priežastį',
          body: 'Laukas „Komentaras“ neprivalomas, bet rekomenduojamas. Tekstas pridedamas prie kiekvieno mokėjimo komentaro (komentaras apkarpomas iki 512 simbolių), kad kolegos vėliau matytų, kodėl mokėjimas atšauktas.',
        },
        lv: {
          title: 'Norādīt anulēšanas iemeslu',
          body: 'Lauks "Comment" nav obligāts, bet ieteicams. Teksts tiek pievienots katra maksājuma komentāram (komentārs tiek saīsināts līdz 512 rakstzīmēm), lai kolēģi vēlāk redzētu, kāpēc tas atcelts.',
        },
      },
    },
    {
      selector: '[data-help="payments-invalidate-confirm"]',
      title: 'Invalidate the payments',
      body: 'Invalidates all ticked payments in one go. Any reminder they paid gets its paid amount reduced again, and for payments cleared from a bank statement the amount returns to the client balance as unallocated money. The payments are kept in the database as deleted, not erased, and the list reloads.',
      section: 'Invalidate payments dialog',
      context: 'invalidate-dialog',
      i18n: {
        lt: {
          title: 'Panaikinti mokėjimus',
          body: '„Invalidate“ vienu kartu panaikina visus pažymėtus mokėjimus. Jų apmokėtų priminimų apmokėta suma vėl sumažinama, o iš banko išrašo užskaitytų mokėjimų suma grįžta į kliento balansą kaip nepaskirstyti pinigai. Mokėjimai duomenų bazėje lieka pažymėti kaip ištrinti, o ne sunaikinti, ir sąrašas atnaujinamas.',
        },
        lv: {
          title: 'Anulēt maksājumus',
          body: '"Invalidate" vienā reizē anulē visus atzīmētos maksājumus. To apmaksāto atgādinājumu samaksātā summa atkal tiek samazināta, un no bankas konta izraksta piesaistīto maksājumu summa atgriežas klienta bilancē kā nesadalīta nauda. Maksājumi datubāzē paliek atzīmēti kā dzēsti, nevis iznīcināti, un saraksts tiek pārlādēts.',
        },
      },
    },
  ],
  i18n: {
    lt: {
      title: 'Mokėjimai',
      intro:
        'Čia patenka kiekvienas įmokos mokėjimas, užskaitytas pagal kliento mokėjimų planą iš banko išrašo arba iš kliento balanso. Filtruokite pagal draudiką ir laikotarpį, tada deklaruokite mokėjimus draudikui arba sugeneruokite draudiko Excel ataskaitą, arba panaikinkite per klaidą užskaitytą mokėjimą.',
      sections: {
        'Getting around': 'Naršymas',
        'Finding payments': 'Mokėjimų paieška',
        'Reading the list': 'Sąrašo skaitymas',
        'Declaring to the insurer': 'Deklaravimas draudikui',
        'Declare payments dialog': 'Langas „Deklaruoti įmokas“',
        'After declaring': 'Po deklaravimo',
        'Invalidate payments dialog': 'Mokėjimų panaikinimo langas',
      },
      contexts: {
        'declare-dialog':
          'Filtre „Draudikas“ pasirinkite draudiką, pažymėkite bent vieną mokėjimą ir įrankių juostoje spustelėkite „Deklaruoti įmokas“.',
        'declare-result': 'Lange „Deklaruoti įmokas“ apačioje spustelėkite mygtuką „Deklaruoti įmokas“.',
        'report-ready': 'Lange „Deklaruoti įmokas“ apačioje spustelėkite „Generate report“.',
        'invalidate-dialog':
          'Pažymėkite vieną ar kelis mokėjimus ir spustelėkite panaikinimo piktogramą pažymėtos eilutės paskutiniame stulpelyje.',
      },
      tips: [
        'Pakeitus bet kurį filtrą (datas, draudiką, klientą, poliso numerį, draudimo liniją, valiutą, mokėjimo tipą, deklaruotų jungiklį ar GrECo įmones) sąrašas iš naujo įkeliamas iš serverio. Paieškos laukas tik susiaurina jau įkeltas eilutes.',
        'Deklaruoti mokėjimai pagal nutylėjimą slepiami. Žmogaus piktograma siaurame stulpelyje šalia žymimojo langelio žymi deklaruotą mokėjimą; užveskite pelę ir pamatysite, kas ir kada jį deklaravo ir ar rankiniu būdu.',
        'Raudonas „Declared premium“ reiškia, kad draudikui deklaruota suma skiriasi nuo užskaitytos įmokos. Raudoni draudiko ataskaitos stulpeliai reiškia, kad susietoje draudiko ataskaitoje nurodyta kita įmoka, komisinis ar komisinio tarifas nei PCT.',
        'Sugeneravus draudiko Excel ataskaitą visi joje esantys mokėjimai pažymimi kaip deklaruoti, lygiai kaip deklaruojant rankiniu būdu. Generuokite ją tik tada, kai tikrai siunčiate ataskaitą draudikui.',
        'Puslapio vieta procese: banko išrašai užskaitomi puslapyje „Banko išrašai“ (arba pinigai paskirstomi iš „Kliento balansas“), užskaityti mokėjimai atsiranda čia, o jų deklaravimas draudikui yra paskutinis žingsnis.',
      ],
    },
    lv: {
      title: 'Maksājumi',
      intro:
        'Šeit nonāk katrs prēmijas maksājums, kas piesaistīts klienta maksājumu plānam no bankas konta izraksta vai no klienta bilances. Filtrējiet pēc apdrošinātāja un perioda, tad deklarējiet maksājumus apdrošinātājam vai ģenerējiet apdrošinātāja Excel atskaiti, vai anulējiet kļūdaini piesaistītu maksājumu.',
      sections: {
        'Getting around': 'Orientēšanās',
        'Finding payments': 'Maksājumu meklēšana',
        'Reading the list': 'Saraksta lasīšana',
        'Declaring to the insurer': 'Deklarēšana apdrošinātājam',
        'Declare payments dialog': 'Logs "Declare payments"',
        'After declaring': 'Pēc deklarēšanas',
        'Invalidate payments dialog': 'Maksājumu anulēšanas logs',
      },
      contexts: {
        'declare-dialog':
          'Filtrā "Insurer" izvēlieties apdrošinātāju, atzīmējiet vismaz vienu maksājumu un rīkjoslā noklikšķiniet "Declare payments".',
        'declare-result': 'Logā "Declare payments" apakšā noklikšķiniet pogu "Declare payments".',
        'report-ready': 'Logā "Declare payments" apakšā noklikšķiniet "Generate report".',
        'invalidate-dialog':
          'Atzīmējiet vienu vai vairākus maksājumus un noklikšķiniet anulēšanas ikonu atzīmētās rindas pēdējā kolonnā.',
      },
      tips: [
        'Mainot jebkuru filtru (datumus, apdrošinātāju, klientu, polises numuru, segmentu, valūtu, maksājuma veidu, deklarēto slēdzi vai GrECo uzņēmumus), saraksts tiek no jauna ielādēts no servera. Meklēšanas lauks tikai sašaurina jau ielādētās rindas.',
        'Deklarētie maksājumi pēc noklusējuma ir paslēpti. Cilvēka ikona šaurajā kolonnā blakus izvēles rūtiņai apzīmē deklarētu maksājumu; novietojiet virs tās peli, lai redzētu, kas un kad to deklarēja un vai manuāli.',
        'Sarkans "Declared premium" nozīmē, ka apdrošinātājam deklarētā summa atšķiras no piesaistītās prēmijas. Sarkanas apdrošinātāja atskaites kolonnas nozīmē, ka saistītajā apdrošinātāja atskaitē norādīta cita prēmija, komisija vai komisijas likme nekā PCT.',
        'Ģenerējot apdrošinātāja Excel atskaiti, visi tajā iekļautie maksājumi tiek atzīmēti kā deklarēti, tieši tāpat kā manuāli deklarējot. Ģenerējiet to tikai tad, kad tiešām sūtāt atskaiti apdrošinātājam.',
        'Lapas vieta procesā: bankas konta izraksti tiek piesaistīti lapā "Bankas konta izraksti" (vai nauda tiek sadalīta no "Client balance"), piesaistītie maksājumi parādās šeit, un to deklarēšana apdrošinātājam ir pēdējais solis.',
      ],
    },
  },
};
